import { useEffect, useRef } from "react";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatClock } from "../logic/dayTime";
import { useReservationFormat } from "../hooks/useReservationFormat";
import {
  SCHEDULE_CELL_MINUTES,
  isCellClosed,
  minuteOfDayIn,
  type ScheduleBar,
  type ScheduleTable,
} from "./scheduleLayout";
import { BAR_TONE } from "./scheduleTones";

// Ширината на един час — половинките са по-широки от пръст и на телефон.
const HOUR_WIDTH_PX = 150;
// Редът е h-14 (56px); лентите са с 6px отстъп отгоре/отдолу и 2px между пътеките.
const ROW_INNER_PX = 44;
const BAR_INSET_PX = 6;
const LANE_GAP_PX = 2;

/** Вертикалното място на лентата по пътеката ѝ. */
const laneBox = (bar: ScheduleBar) => {
  const height = (ROW_INNER_PX - (bar.lanes - 1) * LANE_GAP_PX) / bar.lanes;
  return { top: BAR_INSET_PX + bar.lane * (height + LANE_GAP_PX), height };
};

export interface ScheduleRow {
  table: ScheduleTable;
  bars: ScheduleBar[];
}

/**
 * Решетката „маси × часове": вляво закрепената колона с масите, горе часовете
 * с брояча „резервации | гости", във всеки час две половинки (клик в свободна
 * → нова резервация за масата и часа; минала или след 22:45 — неактивна), отгоре лентите
 * (клик → редакция; застъпващите се — на пътеки) и линията „сега". Скролва
 * вътре в себе си: часовете горе и масите вляво остават закрепени; днес при
 * отваряне се превърта до „сега", без „сега" в прозореца — в началото му.
 * Докато денят се зарежда (`isPending`) е затъмнена и не приема кликове;
 * `highlightId` — току-що записаната резервация, оградена и превъртяна в екрана.
 */
const ScheduleGrid = ({
  rows,
  startHour,
  endHour,
  hourLoads,
  dateKey,
  nowMs,
  showNow,
  isNarrow,
  isPending,
  highlightId,
  onCellClick,
  onBarClick,
}: {
  rows: ScheduleRow[];
  startHour: number;
  // Краят на прозореца (час, без него) — затварянето по работното време.
  endHour: number;
  hourLoads: { count: number; guests: number }[];
  dateKey: string;
  nowMs: number;
  // Линията „сега" (днес, в прозореца).
  showNow: boolean;
  isNarrow: boolean;
  isPending: boolean;
  highlightId: number | null;
  onCellClick: (table: ScheduleTable, minute: number) => void;
  onBarClick: (bar: ScheduleBar) => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const hours = Array.from({ length: endHour - startHour }, (_, index) => startHour + index);
  const spanMinutes = hours.length * 60;
  const percentOf = (minute: number) => ((minute - startHour * 60) / spanMinutes) * 100;
  // По часовника на стената (като колоните) — не по изминали милисекунди; след
  // полунощ в смяната — над 24:00 спрямо деня.
  const nowMinute = minuteOfDayIn(dateKey, nowMs);
  const firstColumn = isNarrow ? "w-[88px] px-3" : "w-[132px] px-4";
  const scrollerRef = useRef<HTMLDivElement>(null);
  const nowRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLButtonElement>(null);
  const nowVisible = showNow && nowMinute >= startHour * 60 && nowMinute <= endHour * 60;

  // Днес — „сега" във видимото (веднъж при смяна на деня/прозореца, не на всяка
  // минута). Без линия „сега" (друг ден или „Скрий до 1-ва резервация" след
  // нея) — от началото на прозореца, иначе скролът остава на стария час и
  // първите колони са зад левия ръб.
  useEffect(() => {
    if (nowRef.current) nowRef.current.scrollIntoView({ inline: "center", block: "nearest" });
    else if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [dateKey, startHour, nowVisible]);
  // Записаната резервация — в екрана; при друг ден лентата идва с данните (след `isPending`).
  useEffect(() => {
    if (highlightId !== null && !isPending) highlightRef.current?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [highlightId, isPending]);

  const barText = (bar: ScheduleBar) => ({
    name: bar.reservation.guestName,
    meta: `${format.guests(bar.reservation.guests)} · ${formatClock(bar.fromMs)}–${formatClock(bar.toMs)}`,
  });

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollerRef}
        aria-busy={isPending}
        className={`min-h-0 flex-1 overflow-auto transition-opacity duration-150 ${isPending ? "pointer-events-none opacity-50" : ""}`}
      >
        <div className="flex min-w-max">
          {/* Масите — закрепени вляво при хоризонталния скрол. */}
          <div className={`sticky left-0 z-[5] shrink-0 border-r border-shade-2 bg-surface ${isNarrow ? "w-[88px]" : "w-[132px]"}`}>
            <div
              className={`sticky top-0 z-[7] flex h-14 items-center border-b border-shade-2 bg-fill text-[11px] font-bold uppercase tracking-[1.2px] text-muted ${firstColumn}`}
            >
              {t("reservationsTable.table")}
            </div>
            {rows.map(({ table }) => (
              <div
                key={table.id}
                className={`flex h-14 flex-col justify-center gap-px border-b border-shade-2 ${firstColumn}`}
              >
                <span className="truncate text-[14px] font-bold leading-[1.2] tabular-nums text-ink" title={table.name}>
                  {format.tableName(table.name)}
                </span>
                <span className="truncate text-[12px] leading-[1.2] text-muted">{format.seats(table.seats)}</span>
              </div>
            ))}
          </div>

          <div className="relative flex-1" style={{ minWidth: hours.length * HOUR_WIDTH_PX }}>
            <div className="sticky top-0 z-[4] flex h-14 border-b border-shade-2 bg-fill">
              {/* Етикетът „сега" — в закрепения ред с часовете, на долния му ръб (линията е отдолу). */}
              {nowVisible && (
                <span
                  className="pointer-events-none absolute -bottom-2.5 z-[1] -translate-x-1/2 whitespace-nowrap rounded-md bg-brand px-1.5 py-0.5 font-pos-mono text-[10.5px] font-bold tabular-nums text-white"
                  style={{ left: `${percentOf(nowMinute)}%` }}
                >
                  {t("reservationsTable.now", { time: formatClock(nowMs) })}
                </span>
              )}
              {hours.map((hour, index) => {
                const load = hourLoads[index];
                return (
                  <div
                    key={hour}
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 border-r border-shade-2"
                  >
                    <span className="font-pos-mono text-[14px] font-bold tabular-nums text-ink">
                      {String(hour % 24).padStart(2, "0")}
                    </span>
                    <span
                      title={t("reservationsTable.hourLoadHint")}
                      className={`font-pos-mono text-[11px] tabular-nums ${load?.count ? "text-brand-deep-ink" : "text-muted"}`}
                    >
                      {load?.count ? `${load.count} | ${load.guests}` : "—"}
                    </span>
                  </div>
                );
              })}
            </div>

            {rows.map(({ table, bars }) => (
              <div key={table.id} className="relative flex h-14 border-b border-shade-2">
                {hours.map((hour) => (
                  <div key={hour} className="flex min-w-0 flex-1 border-r border-shade-2">
                    {[0, SCHEDULE_CELL_MINUTES].map((offset) => {
                      const minute = hour * 60 + offset;
                      const past = isCellClosed(dateKey, minute, nowMs);
                      return (
                        <button
                          key={offset}
                          type="button"
                          disabled={past}
                          aria-label={t("reservationsTable.newAt", {
                            table: format.tableName(table.name),
                            time: `${String(hour % 24).padStart(2, "0")}:${offset ? "30" : "00"}`,
                          })}
                          onClick={() => onCellClick(table, minute)}
                          className={`group flex min-w-0 flex-1 cursor-pointer items-center justify-center border-none p-1 transition-colors duration-150 disabled:cursor-default ${
                            offset === 0 ? "border-r border-dashed border-shade-1" : ""
                          } ${past ? "bg-fill/60" : "bg-transparent hover:bg-brand-tint"}`}
                        >
                          {!past && (
                            <PlusOutlined className="text-[12px] text-brand-deep-ink opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}

                {bars.map((bar) => {
                  const text = barText(bar);
                  const box = laneBox(bar);
                  const highlighted = bar.reservation.id === highlightId;
                  return (
                    <button
                      key={bar.key}
                      ref={highlighted ? highlightRef : undefined}
                      type="button"
                      title={`${text.name} · ${text.meta}`}
                      onClick={() => onBarClick(bar)}
                      className={`absolute z-[2] flex min-w-0 cursor-pointer flex-col justify-center gap-px overflow-hidden rounded-[8px] px-2.5 py-0 text-left ${BAR_TONE[bar.tone]} ${
                        highlighted ? "ring-2 ring-brand ring-offset-2 ring-offset-surface" : ""
                      }`}
                      style={{
                        top: box.top,
                        height: box.height,
                        left: `calc(${percentOf(bar.startMinute)}% + 2px)`,
                        width: `calc(${percentOf(bar.endMinute) - percentOf(bar.startMinute)}% - 4px)`,
                      }}
                    >
                      <span className="truncate text-[12.5px] font-semibold leading-[1.25]">{text.name}</span>
                      {bar.lanes === 1 && (
                        <span className="truncate font-pos-mono text-[10.5px] leading-[1.25] tabular-nums">
                          {text.meta}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}

            {nowVisible && (
              <div
                ref={nowRef}
                className="pointer-events-none absolute bottom-0 top-0 z-[3] w-0 border-l-[1.5px] border-brand"
                style={{ left: `${percentOf(nowMinute)}%` }}
              />
            )}
          </div>
        </div>
      </div>
      {isPending && (
        <div className="pointer-events-none absolute inset-x-0 top-20 flex justify-center">
          <span className="flex items-center gap-2 rounded-full border border-shade-2 bg-surface px-4 py-2 text-[13px] font-semibold text-body shadow-[0_8px_24px_-8px_rgba(28,25,23,0.3)]">
            <LoadingOutlined />
            {t("common.loading")}
          </span>
        </div>
      )}
    </div>
  );
};

export default ScheduleGrid;
